import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Table, Divider, Row, Col, InputNumber, Tooltip, Space } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { FormattedMessage } from 'react-intl';
import PositionBadge from '../PositionBadge';
import { SelectSilences } from './';
import { getMarkers, getMarkerEndpoints } from '../../../utils/adsMarker';
import { intents, variants } from '../../styled-antd/utils';
import FormatUtils from '../../../utils/FormatUtils';
import MinMaxHelper from './MinMaxHelper';
import { useGetShow } from '../../hooks/query/show';
import Loading from '../../ui/Loading';
import { StyledButton } from '../../styled-antd';
import useMessage from '../../hooks/useMessage';

const AdsMarkerTable = ({
  markers = [],
  silenceDetected,
  handleMarkerChange,
  episodeDuration,
  handleAddMarker,
  handleRemoveMarker,
  setSeek,
}) => {
  const { show, isShowLoading } = useGetShow();
  const { message } = useMessage();

  const sortedMarkers = getMarkers(markers, episodeDuration, show);

  const columns = useMemo(
    () => [
      {
        title: <FormattedMessage defaultMessage="Markers type" />,
        dataIndex: 'placement',
        key: 'type',
        render: function renderPlacement(placement, marker) {
          const markerType =
            placement === 'preroll' || placement === 'postroll' ? placement : 'midroll';
          return (
            <div style={{ display: 'inline-block' }} onClick={() => setSeek(marker.start - 2)}>
              <PositionBadge type={placement} label={markerType} disable={!marker.placement} />
            </div>
          );
        },
      },
      {
        title: <FormattedMessage defaultMessage="Position" />,
        dataIndex: 'start',
        key: 'position',
        render: function renderPostionStart(start, { placement }) {
          const isEnabled = markers.find((marker) => placement === marker.placement);

          if (!isEnabled) {
            return null;
          }

          const [minStart, maxStart] = getMarkerEndpoints(markers, placement, episodeDuration);

          const handleTimeChange = (value) => {
            if (value >= minStart && value <= maxStart) {
              return handleMarkerChange(placement, value);
            }
            if (value < minStart) {
              message.warning({
                content: (
                  <FormattedMessage
                    defaultMessage="Your marker cannot be placed before {minStart}"
                    values={{ minStart: FormatUtils.formatTimeHundredthSeconds(minStart) }}
                  />
                ),
              });
              return handleMarkerChange(placement, minStart);
            }
            if (value > maxStart) {
              message.warning({
                content: (
                  <FormattedMessage
                    defaultMessage="Your marker cannot be placed after {maxStart}"
                    values={{ maxStart: FormatUtils.formatTimeHundredthSeconds(maxStart) }}
                  />
                ),
              });

              return handleMarkerChange(placement, maxStart);
            }
          };

          return (
            <Space>
              <InputNumber
                style={{ width: '120px' }}
                value={Math.min(start, episodeDuration)}
                onBlur={(event) => handleTimeChange(FormatUtils.hhmmssToSs(event.target.value))}
                onStep={(value) => handleTimeChange(value)}
                formatter={(value) => FormatUtils.formatTimeHundredthSeconds(value)}
                parser={(value) => FormatUtils.hhmmssToSs(value)}
                min={minStart}
                max={maxStart}
              />
              <Tooltip
                title={
                  <MinMaxHelper
                    markers={markers}
                    markerId={placement}
                    episodeDuration={episodeDuration}
                  />
                }
              >
                <QuestionCircleOutlined />
              </Tooltip>
            </Space>
          );
        },
      },
      {
        title: <FormattedMessage defaultMessage="Silent positions" />,
        dataIndex: 'start',
        key: 'position',
        render: function renderSilencesPosition(position, currentMarker) {
          const isEnabled = markers.find((marker) => currentMarker.placement === marker.placement);

          return (
            isEnabled &&
            silenceDetected && (
              <SelectSilences
                silenceDetected={silenceDetected}
                handleMarkerChange={handleMarkerChange}
                marker={currentMarker}
                currentPosition={position}
                markers={markers}
                episodeDuration={episodeDuration}
              />
            )
          );
        },
      },
      {
        dataIndex: 'placement',
        key: 'action',
        render: function renderActivateButton(placement) {
          // Only show the activate button for midroll markers
          if (placement !== 'midroll' && placement !== 'midroll2') {
            return null;
          }

          const isEnabled = markers.find((marker) => placement === marker.placement);

          return (
            <Row gutter={16} justify="end">
              <Col>
                <StyledButton
                  variant={variants.text}
                  intent={intents.primary}
                  onClick={
                    isEnabled
                      ? () => handleRemoveMarker(placement)
                      : () => handleAddMarker(placement)
                  }
                >
                  {isEnabled ? (
                    <FormattedMessage defaultMessage="Deactivate" />
                  ) : (
                    <FormattedMessage defaultMessage="Activate" />
                  )}
                </StyledButton>
              </Col>
            </Row>
          );
        },
      },
    ],
    [
      setSeek,
      markers,
      episodeDuration,
      handleMarkerChange,
      message,
      silenceDetected,
      handleRemoveMarker,
      handleAddMarker,
    ]
  );

  return isShowLoading ? (
    <Loading />
  ) : (
    <div>
      <Divider />
      <Table
        rowClassName="no-pointer"
        columns={columns}
        dataSource={sortedMarkers}
        pagination={false}
        rowKey={(record) => {
          return `${record.placement}-${record.start}`;
        }}
        locale={{
          emptyText: <FormattedMessage defaultMessage="No Ad Markers" />,
        }}
      />
    </div>
  );
};

AdsMarkerTable.propTypes = {
  handleMarkerChange: PropTypes.func.isRequired,
  handleRemoveMarker: PropTypes.func.isRequired,
  markers: PropTypes.array.isRequired,
  silenceDetected: PropTypes.array,
  episodeDuration: PropTypes.number.isRequired,
  handleAddMarker: PropTypes.func.isRequired,
  setSeek: PropTypes.func.isRequired,
};

export default AdsMarkerTable;
